"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties
} from "react";
import { calculateItemConstraints } from "../../global/dom/calculateItemConstraints";
import { mountAxis } from "../../global/mountAxis";
import {
  getMountedAxisState,
  getRegisteredAxis,
  subscribeToMountedAxis,
  updateMountedAxis
} from "../../global/mutable-state/axes";
import { getInteractionState } from "../../global/mutable-state/interactions";
import type {
  RegisteredResizeAxis,
  RegisteredResizeItem,
  RegisteredSeparator
} from "../../global/types";
import { layoutsEqual } from "../../global/utils/layoutsEqual";
import { recordAxisLayoutChange } from "../../global/utils/recordAxisLayoutChange";
import { useForceUpdate } from "../../hooks/useForceUpdate";
import { useId } from "../../hooks/useId";
import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect";
import { useMergedRefs } from "../../hooks/useMergedRefs";
import { useStableCallback } from "../../hooks/useStableCallback";
import { useStableObject } from "../../hooks/useStableObject";
import { getTestId } from "../../utils/getTestId";
import type { SeparatorOverlayProps } from "../separator/types";
import { GroupContext } from "./GroupContext";
import { isInDocumentOrder } from "./isInDocumentOrder";
import { ResizePreview } from "./ResizePreview";
import { sortByDocumentPosition } from "./sortByDocumentPosition";
import { sortByElementOffset } from "./sortByElementOffset";
import { ParentGroupContext } from "./ParentGroupContext";
import type { GroupProps, Layout, ParentGroup } from "./types";
import { useGroupImperativeHandle } from "./useGroupImperativeHandle";
import { useResizePreviews } from "./useResizePreviews";

/**
 * A Group wraps a set of resizable Panel components.
 * Group content can be resized _horizontally_ or _vertically_.
 *
 * Group elements always include the following attributes:
 *
 * ```html
 * <div data-group data-testid="group-id-prop" id="group-id-prop">
 * ```
 *
 * ℹ️ [Test id](https://testing-library.com/docs/queries/bytestid/) can be used to narrow selection when unit testing.
 */
export function Group({
  children,
  className,
  defaultLayout,
  disableCursor,
  disabled,
  elementRef: elementRefProp,
  groupRef,
  id: idProp,
  onLayoutChange: onLayoutChangeUnstable,
  onLayoutChanged: onLayoutChangedUnstable,
  orientation = "horizontal",
  resizePreviewMode = "panel",
  resizeTargetMinimumSize = {
    coarse: 20,
    fine: 10
  },
  style,
  ...rest
}: GroupProps) {
  const prevLayoutRef = useRef<{
    onLayoutChange: Layout;
    onLayoutChanged: Layout;
  }>({
    onLayoutChange: {},
    onLayoutChanged: {}
  });

  const onLayoutChangeStable = useStableCallback((layout: Layout) => {
    if (layoutsEqual(prevLayoutRef.current.onLayoutChange, layout)) {
      // Memoize callback
      return;
    }

    prevLayoutRef.current.onLayoutChange = layout;
    onLayoutChangeUnstable?.(layout);
  });

  const onLayoutChangedStable = useStableCallback(
    (layout: Layout, isUserInteraction: boolean, requestedLayout: Layout) => {
      // Only the validated layout is memoized; requested layout changes alone don't trigger the callback
      if (layoutsEqual(prevLayoutRef.current.onLayoutChanged, layout)) {
        // Memoize callback
        return;
      }

      prevLayoutRef.current.onLayoutChanged = layout;
      onLayoutChangedUnstable?.(layout, { isUserInteraction, requestedLayout });
    }
  );

  const id = useId(idProp);

  const [overlay, setOverlay] = useState<SeparatorOverlayProps>();

  const previews = useResizePreviews({
    groupId: id,
    resizePreviewMode
  });

  const elementRef = useRef<HTMLDivElement | null>(null);

  const [panelOrSeparatorChangeSigil, forceUpdate] = useForceUpdate();

  // DOM order of registered Panels/Separators, as of the last time the Group was (re)mounted;
  // used to detect children that have been moved without re-registering (see below)
  const domOrderRef = useRef<HTMLElement[]>([]);

  const inMemoryValuesRef = useRef<{
    lastExpandedPanelSizes: { [panelIds: string]: number };
    layouts: { [panelIds: string]: Layout };
    panels: RegisteredResizeItem[];
    separators: RegisteredSeparator[];
  }>({
    lastExpandedPanelSizes: {},
    layouts: {},
    panels: [],
    separators: []
  });

  const mergedRef = useMergedRefs(elementRef, elementRefProp);

  const imperativeGroupRef = useGroupImperativeHandle(id, groupRef);

  // Methods are delegated (rather than copied) because the imperative handle is populated during a layout effect
  const parentGroup = useMemo<ParentGroup>(
    () => ({
      getLayout: () => imperativeGroupRef.current.getLayout(),
      id,
      orientation,
      setLayout: (layout) => imperativeGroupRef.current.setLayout(layout)
    }),
    [id, imperativeGroupRef, orientation]
  );

  // TRICKY Don't read for state; it will always lag behind by one tick
  const getPanelStyles = useStableCallback(
    (groupId: string, panelId: string) => {
      const groupState = getMountedAxisState(groupId);
      if (groupState) {
        return {
          flexGrow: groupState.layout[panelId] ?? 1
        } satisfies CSSProperties;
      }

      // This is unexpected except for the initial mount (before the group has registered with the global store)
      if (defaultLayout?.[panelId]) {
        return {
          flexGrow: defaultLayout?.[panelId]
        } satisfies CSSProperties;
      }
    }
  );

  const stableProps = useStableObject({
    defaultLayout,
    disableCursor,
    resizeTargetMinimumSize
  });

  const context = useMemo(
    () => ({
      get disableCursor() {
        return !!stableProps.disableCursor;
      },
      getPanelStyles,
      id,
      orientation,
      registerPanel: (panel: RegisteredResizeItem) => {
        const inMemoryValues = inMemoryValuesRef.current;
        inMemoryValues.panels = sortByElementOffset(orientation, [
          ...inMemoryValues.panels,
          panel
        ]);

        forceUpdate();

        return () => {
          inMemoryValues.panels = inMemoryValues.panels.filter(
            (current) => current !== panel
          );

          forceUpdate();
        };
      },
      registerOverlay: (props: SeparatorOverlayProps) => {
        setOverlay(props);

        return () => {
          setOverlay(undefined);
        };
      },
      registerSeparator: (separator: RegisteredSeparator) => {
        const inMemoryValues = inMemoryValuesRef.current;
        inMemoryValues.separators = sortByElementOffset(orientation, [
          ...inMemoryValues.separators,
          separator
        ]);

        forceUpdate();

        return () => {
          inMemoryValues.separators = inMemoryValues.separators.filter(
            (current) => current !== separator
          );

          forceUpdate();
        };
      },
      updatePanelProps: (
        panelId: string,
        { disabled }: { disabled: boolean | undefined }
      ) => {
        const inMemoryValues = inMemoryValuesRef.current;
        const panel = inMemoryValues.panels.find(
          (current) => current.id === panelId
        );
        if (!panel || panel.constraintProps.disabled === disabled) {
          return;
        }
        panel.constraintProps.disabled = disabled;

        const group = getRegisteredAxis(id);
        const groupState = getMountedAxisState(id);
        if (group && groupState) {
          updateMountedAxis(group, {
            ...groupState,
            derivedItemConstraints: calculateItemConstraints(group)
          });
        }
      },
      updateSeparatorProps: (
        separatorId: string,
        {
          disabled,
          disableDoubleClick
        }: {
          disabled: boolean | undefined;
          disableDoubleClick: boolean | undefined;
        }
      ) => {
        const inMemoryValues = inMemoryValuesRef.current;
        const separator = inMemoryValues.separators.find(
          (current) => current.id === separatorId
        );
        if (separator) {
          separator.disabled = disabled;
          separator.disableDoubleClick = disableDoubleClick;
        }
      }
    }),
    [getPanelStyles, id, forceUpdate, orientation, stableProps]
  );

  const registeredGroupRef = useRef<RegisteredResizeAxis | null>(null);

  // Register Group and child Panels/Separators with global state
  // Listen to global state for drag state related to this Group
  useIsomorphicLayoutEffect(() => {
    const element = elementRef.current;
    if (element === null) {
      return;
    }

    const inMemoryValues = inMemoryValuesRef.current;

    // Keyed Panels/Separators can be moved without re-registering (e.g. reordered children),
    // so registration order may be stale; re-sort before deriving constraints from it.
    inMemoryValues.panels = sortByElementOffset(
      orientation,
      inMemoryValues.panels
    );
    inMemoryValues.separators = sortByElementOffset(
      orientation,
      inMemoryValues.separators
    );
    domOrderRef.current = sortByDocumentPosition([
      ...inMemoryValues.panels.map(({ element }) => element),
      ...inMemoryValues.separators.map(({ element }) => element)
    ]);

    // Guard against unexpected layout attribute ordering by pre-sorting panel ids/keys; see issues/656
    let preSortedDefaultLayout: Layout | undefined = undefined;
    if (stableProps.defaultLayout !== undefined) {
      if (
        Object.keys(stableProps.defaultLayout).length ===
        inMemoryValues.panels.length
      ) {
        preSortedDefaultLayout = {};
        for (const panel of inMemoryValues.panels) {
          const size = stableProps.defaultLayout[panel.id];
          if (size !== undefined) {
            preSortedDefaultLayout[panel.id] = size;
          }
        }
      }
    }

    const group: RegisteredResizeAxis = {
      disabled: !!disabled,
      element,
      id,
      mutableState: {
        defaultLayout: preSortedDefaultLayout,
        disableCursor: !!stableProps.disableCursor,
        expandedItemSizes: inMemoryValuesRef.current.lastExpandedPanelSizes,
        layouts: inMemoryValuesRef.current.layouts
      },
      orientation,
      items: inMemoryValues.panels,
      resizePreviewMode,
      get resizeTargetMinimumSize() {
        return stableProps.resizeTargetMinimumSize;
      },
      separators: inMemoryValues.separators
    };

    registeredGroupRef.current = group;

    const unmountGroup = mountAxis(group);

    const {
      defaultLayoutDeferred,
      derivedItemConstraints: derivedPanelConstraints,
      layout,
      requestedLayout
    } = getMountedAxisState(group.id, true);

    if (!defaultLayoutDeferred && derivedPanelConstraints.length > 0) {
      try {
        // Either handler may throw; onLayoutChanged should still be called if onLayoutChange throws
        try {
          onLayoutChangeStable(layout);
        } finally {
          // Initial mount is not a user interaction (#716).
          onLayoutChangedStable(layout, false, requestedLayout);
        }
      } catch (error) {
        // An effect that throws can't return a cleanup function, so unmount the group before re-throwing
        registeredGroupRef.current = null;
        unmountGroup();

        throw error;
      }
    }

    const removeChangeEventListener = subscribeToMountedAxis(id, (event) => {
      const {
        defaultLayoutDeferred,
        derivedItemConstraints: derivedPanelConstraints,
        layout,
        requestedLayout
      } = event.next;

      if (defaultLayoutDeferred || derivedPanelConstraints.length === 0) {
        // This indicates that the Group has not finished mounting yet
        // Likely because it has been rendered inside of a hidden DOM subtree
        // Ignore layouts in this case because they will not have been validated
        return;
      }

      recordAxisLayoutChange({
        derivedItemConstraints: derivedPanelConstraints,
        axis: group,
        layout,
        prevLayout: event.prev?.layout,
        requestedLayout
      });

      // Lastly notify layout-change(d) handlers of the update
      const interactionState = getInteractionState();
      const isCompleted =
        interactionState.state !== "active" ||
        !interactionState.hitRegions.some((region) => region.axis === group);
      // Either handler may throw; onLayoutChanged should still be called if onLayoutChange throws
      try {
        onLayoutChangeStable(layout);
      } finally {
        if (isCompleted) {
          onLayoutChangedStable(
            layout,
            event.isUserInteraction,
            requestedLayout
          );
        }
      }
    });

    return () => {
      registeredGroupRef.current = null;

      unmountGroup();
      removeChangeEventListener();
    };
  }, [
    disabled,
    id,
    onLayoutChangedStable,
    onLayoutChangeStable,
    orientation,
    panelOrSeparatorChangeSigil,
    resizePreviewMode,
    stableProps
  ]);

  // Keyed Panels/Separators can be moved (e.g. reordered) without re-registering;
  // when that happens, remount the Group so their cached order is re-sorted.
  // This compares DOM order (rather than element offsets) because DOM order and visual order may differ,
  // e.g. "flex-direction: row-reverse" or CSS "order"; comparing against the offset-sorted order would never settle.
  // Additions and removals are handled by registration, so this only needs to check order.
  useIsomorphicLayoutEffect(() => {
    if (!isInDocumentOrder(domOrderRef.current)) {
      forceUpdate();
    }
  });

  // Not all props require re-registering the group;
  // Some can be updated after the group has been registered
  useEffect(() => {
    const registeredGroup = registeredGroupRef.current;
    if (registeredGroup) {
      registeredGroup.mutableState.defaultLayout = defaultLayout;
      registeredGroup.mutableState.disableCursor = !!disableCursor;
    }
  });

  return (
    <ParentGroupContext.Provider value={parentGroup}>
      <GroupContext.Provider value={context}>
        <div
          {...rest}
          className={className}
          data-group
          data-testid={getTestId(rest, idProp)}
          id={id}
          ref={mergedRef}
          style={{
            height: "100%",
            width: "100%",
            overflow: "hidden",
            position:
              resizePreviewMode === "separator" ? "relative" : undefined,

            ...style,

            display: "flex",
            flexDirection: orientation === "horizontal" ? "row" : "column",
            flexWrap: "nowrap",

            // Inform the browser that the library is handling touch events for this element
            // but still allow users to scroll content within panels in the non-resizing direction
            // NOTE This is not an inherited style
            // See github.com/bvaughn/react-resizable-panels/issues/662
            touchAction: orientation === "horizontal" ? "pan-y" : "pan-x"
          }}
        >
          {children}
          {previews.map((preview) => (
            <ResizePreview
              key={preview.key}
              overlay={overlay}
              preview={preview}
            />
          ))}
        </div>
      </GroupContext.Provider>
    </ParentGroupContext.Provider>
  );
}

// See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Function/displayName
Group.displayName = "Group";
